import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import history from 'src/history'

// Components
import { Button, Text, If, Unless } from 'src/Components'
import {
  IconThumbsUp,
  IconThumbsDown,
  IconWrench
} from 'src/Components/shared/Icon'

// Selectors
import { hasCustomerToken } from 'src/auth'
import { selectClientFetchId } from 'src/store/selectors/client'
import { selectHomeId } from 'store/selectors/home'

// Actions
import { handleAcknowledgeTuneValue } from 'src/actions/homeowner'

// Utils
import { PATH_HELPERS } from 'src/constants/navigation'
import { dispatchEvent } from 'src/helpers/tagManager'
import {
  LowConfidenceWarnLoad,
  LowConfidenceWarnTuneClick,
  LowConfidenceLooksGoodClick,
  LowConfidenceThumbsDownClick
} from 'src/constants/trackingEvents'

// Styles
import styles from '../Chart.module.scss'
import lcStyles from './LowConfidenceToolTipWarning.module.scss'

const propTypes = { setForceHide: PropTypes.func }

const LowConfidenceToolTipWarning = ({ setForceHide }) => {
  const dispatch = useDispatch()
  const [thumbsDown, setThumbsDown] = useState(false)
  const clientId = useSelector(selectClientFetchId)
  const homeId = useSelector(selectHomeId)

  const handleLooksGood = () => {
    setForceHide(true)
    if (hasCustomerToken()) return

    return () => {
      dispatchEvent(LowConfidenceLooksGoodClick)
      dispatch(handleAcknowledgeTuneValue())
    }
  }

  const handleThumbsDown = () => {
    dispatchEvent(LowConfidenceThumbsDownClick)
    setThumbsDown(true)
  }

  const handleTuneYourValue = () => {
    dispatchEvent(LowConfidenceWarnTuneClick)
    history.push(PATH_HELPERS.tuneYourValue.buildPath(clientId, homeId))
  }

  useEffect(() => {
    dispatchEvent(LowConfidenceWarnLoad)
  }, [])

  return (
    <>
      <Text
        weight='semibold'
        upperspaced
        className={classnames(styles.title, lcStyles.title)}
      >
        <Unless condition={thumbsDown}>
          <FormattedMessage
            id='Homeowner.estimatedValue.lowConfidenceWarning.thumbsUp.title'
            defaultMessage='Quick check:'
          />
        </Unless>
        <If condition={thumbsDown}>
          <FormattedMessage
            id='Homeowner.estimatedValue.lowConfidenceWarning.thumbsDown.title'
            defaultMessage='No worries'
          />
        </If>
      </Text>

      <Text
        size='xs'
        color='quiet'
        className={classnames(styles.describe, lcStyles.describe)}
      >
        <Unless condition={thumbsDown}>
          <FormattedMessage
            id='Homeowner.estimatedValue.lowConfidenceWarning.thumbsUp.subTitle'
            defaultMessage='Does your value seem right?'
          />
        </Unless>
        <If condition={thumbsDown}>
          <FormattedMessage
            id='Homeowner.estimatedValue.lowConfidenceWarning.thumbsDown.subTitle'
            defaultMessage="Let's change it together!"
          />
        </If>
      </Text>

      <Button.Group className={lcStyles.buttonGroup}>
        <Unless condition={thumbsDown}>
          <Button
            data-qa='low-confidence-thumbs-up-btn'
            iconStart={IconThumbsUp}
            onClick={handleLooksGood}
          />
          <Button
            data-qa='low-confidence-thumbs-down-btn'
            iconStart={IconThumbsDown}
            onClick={handleThumbsDown}
          />
        </Unless>
        <If condition={thumbsDown}>
          <Button iconStart={IconWrench} onClick={handleTuneYourValue}>
            <FormattedMessage
              data-qa='low-confidence-tune-value-btn'
              id='Homeowner.estimatedValue.lowConfidenceWarning.tuneBtn'
              defaultMessage='Tune your value'
            />
          </Button>
        </If>
      </Button.Group>
    </>
  )
}

LowConfidenceToolTipWarning.propTypes = propTypes
export default LowConfidenceToolTipWarning
